/**
 * EditorOpzioni — le opzioni di una domanda a scelta: testo, codice, «esclude le altre».
 *
 * Il codice segue il testo (`idDaEtichetta`) finché la persona non lo scrive a mano; se il
 * questionario ha già risposte, i codici esistenti sono bloccati (si cambia solo il testo).
 */

import React from 'react';
import { Box, Checkbox, FormControlLabel, IconButton, Stack, Tooltip, Typography, Button } from '@mui/material';
import { Add, ArrowDownward, ArrowUpward, DeleteOutline, LockOutlined } from '@mui/icons-material';
import { tokens } from '../../../theme';
import { CampoTesto } from './CampoTesto';
import { codiceUnico, nuovaOpzione, type BozzaOpzione } from './bozza';

interface Props {
  opzioni: BozzaOpzione[];
  /** Solo nelle scelte multiple ha senso «esclude le altre». */
  multipla: boolean;
  /** I codici di opzione che non si cambiano più (questionario con risposte). */
  bloccati: Set<string>;
  onChange: (opzioni: BozzaOpzione[]) => void;
}

export const EditorOpzioni: React.FC<Props> = ({ opzioni, multipla, bloccati, onChange }) => {
  const aggiorna = (indice: number, patch: Partial<BozzaOpzione>) => {
    onChange(opzioni.map((o, i) => (i === indice ? { ...o, ...patch } : o)));
  };

  const cambiaEtichetta = (indice: number, etichetta: string) => {
    const o = opzioni[indice];
    const patch: Partial<BozzaOpzione> = { etichetta };
    if (!o.idFisso && !bloccati.has(o.id)) {
      const altri = opzioni.filter((_, i) => i !== indice).map((x) => x.id);
      patch.id = etichetta.trim() ? codiceUnico(etichetta, altri) : '';
    }
    aggiorna(indice, patch);
  };

  const sposta = (indice: number, verso: -1 | 1) => {
    const arrivo = indice + verso;
    if (arrivo < 0 || arrivo >= opzioni.length) return;
    const copia = [...opzioni];
    [copia[indice], copia[arrivo]] = [copia[arrivo], copia[indice]];
    onChange(copia);
  };

  return (
    <Box>
      <Typography variant="caption" sx={{ color: tokens.color.ash, fontWeight: 600 }}>
        Opzioni (almeno due)
      </Typography>
      <Stack spacing={1} sx={{ mt: 0.75 }}>
        {opzioni.map((o, i) => {
          const bloccato = bloccati.has(o.id);
          return (
            <Stack key={o.chiave} direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <CampoTesto
                label={`Opzione ${i + 1}`}
                value={o.etichetta}
                onChange={(v) => cambiaEtichetta(i, v)}
                sx={{ flex: 2 }}
              />
              <CampoTesto
                label="Codice"
                value={o.id}
                onChange={(v) => aggiorna(i, { id: v.toLowerCase().replace(/[^a-z0-9_]/g, '_'), idFisso: true })}
                disabled={bloccato}
                helperText={bloccato ? 'Ha già risposte: non si cambia' : undefined}
                InputProps={{
                  sx: { fontFamily: tokens.font.mono, fontSize: '0.8rem' },
                  endAdornment: bloccato ? <LockOutlined fontSize="small" sx={{ color: tokens.color.mist }} /> : undefined,
                }}
                sx={{ flex: 1, minWidth: 150 }}
              />
              {multipla && (
                <Tooltip title="Sceglierla toglie le altre (per esempio «Nessuno strumento preciso»)">
                  <FormControlLabel
                    sx={{ mr: 0, flexShrink: 0 }}
                    control={<Checkbox size="small" checked={o.esclusiva === true} onChange={(e) => aggiorna(i, { esclusiva: e.target.checked || undefined })} />}
                    label={<Typography variant="caption">Esclude le altre</Typography>}
                  />
                </Tooltip>
              )}
              <Stack direction="row" sx={{ flexShrink: 0 }}>
                <IconButton size="small" onClick={() => sposta(i, -1)} disabled={i === 0} aria-label="Sposta su">
                  <ArrowUpward fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => sposta(i, 1)} disabled={i === opzioni.length - 1} aria-label="Sposta giù">
                  <ArrowDownward fontSize="small" />
                </IconButton>
                <Tooltip title={bloccato ? 'Le risposte già date restano nelle statistiche col loro testo' : 'Rimuovi opzione'}>
                  <IconButton
                    size="small"
                    sx={{ color: tokens.color.danger }}
                    onClick={() => onChange(opzioni.filter((_, x) => x !== i))}
                    aria-label="Rimuovi opzione"
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Stack>
          );
        })}
        <Button
          size="small"
          startIcon={<Add />}
          onClick={() => onChange([...opzioni, nuovaOpzione('', opzioni.map((o) => o.id))])}
          sx={{ alignSelf: 'flex-start' }}
        >
          Aggiungi opzione
        </Button>
      </Stack>
    </Box>
  );
};
